/**
 * Was von einem gescheiterten Aufruf vor den Nutzer darf — immer ein Satz.
 *
 * Über achtzig Stellen schrieben `extractErrorDetail(err) || err.message`. Das
 * `err.message` ist bei Axios englischer Entwicklertext: Ein 422 aus dem
 * Anlege-Assistenten der Accounts stand am 15.09.2026 als „Request failed with
 * status code 422" im Dialog (die E-Mail `chef@acme.test` hatte der
 * E-Mail-Validator als reservierte Domain abgelehnt — welches Feld, sagte die
 * Oberfläche nicht). Regel #23: nie einen Maschinentext zeigen.
 *
 * Die Reihenfolge, in der gesucht wird:
 *
 * 1. die Prosa des Backends (`extractErrorDetail`, lokalisiert),
 * 1b. fehlende Credits (402 `plan_limit_reached:credits:…`, FLEET_CREDITS_SPEC
 *    §4.4) als Satz mit dem Fehlbetrag (`creditsFehlen`),
 * 2. bei einem 422 die beanstandeten FELDER mit den Beschriftungen des
 *    Formulars (`validierungsFelder` + `feldNamen`),
 * 3. keine Antwort (Netz, Zeitüberschreitung),
 * 4. der HTTP-Status in Worten (403, 404, 5xx),
 * 5. ein allgemeiner Satz — dann ist nichts Genaueres bekannt, und der Fehler
 *    geht laut in die Konsole, damit die Ursache nicht verschwindet (Regel #7).
 *
 * `err.message` wird nie gelesen. Ein Wächter hält das fest
 * (`test_fehlertext_ohne_rohtext_canon.py`).
 */
import { errorStatus, extractErrorDetail, validierungsFelder } from '../shared/errorDetail';
import { creditsFehlen, creditsFehlenSatz } from './creditsFehlen';

export type Uebersetzer = (
  key: string,
  defaultText?: string,
  variables?: Record<string, string | number>,
) => string;

/** Feldname der Backend-Antwort → [Schlüssel, Vorgabe] der Beschriftung im Formular. */
export type FeldNamen = Readonly<Record<string, readonly [string, string]>>;

/** Keine Antwort erhalten: Axios setzt dann `request`, aber kein `response`. */
function ohneAntwort(err: unknown): boolean {
  if (err == null || typeof err !== 'object') return false;
  const o = err as { response?: unknown; request?: unknown; code?: unknown; isAxiosError?: unknown };
  if (o.response) return false;
  return o.request !== undefined
    || o.code === 'ERR_NETWORK'
    || o.code === 'ECONNABORTED'
    || o.code === 'ETIMEDOUT';
}

export function fehlerText(err: unknown, t: Uebersetzer, feldNamen: FeldNamen = {}): string {
  const prosa = extractErrorDetail(err);
  if (prosa) return prosa;

  const fehlt = creditsFehlen(err);
  if (fehlt) return creditsFehlenSatz(fehlt, t);

  const felder = validierungsFelder(err);
  if (felder.length) {
    const bekannt = felder.filter((f) => feldNamen[f]);
    const unbekannt = felder.filter((f) => !feldNamen[f]);
    if (unbekannt.length) {
      // Ein Feld ohne Beschriftung ist ein Loch in `feldNamen` der Seite — der
      // rohe Bezeichner gehört nicht vor den Nutzer, aber ins Protokoll.
      console.error('Validierungsfehler für Felder ohne Beschriftung:', unbekannt, err);
    }
    if (bekannt.length) {
      return t('errors.pruefe_felder', 'Bitte prüfe: {felder}', {
        felder: bekannt.map((f) => t(feldNamen[f][0], feldNamen[f][1])).join(', '),
      });
    }
    return t('errors.eingabe_abgelehnt', 'Eine Eingabe wurde nicht angenommen. Bitte prüfe das Formular.');
  }

  if (ohneAntwort(err)) {
    return t('errors.keine_antwort', 'Der Server hat nicht geantwortet. Prüfe deine Verbindung und versuch es noch einmal.');
  }

  const status = errorStatus(err);
  if (status === 403) {
    return t('errors.keine_berechtigung', 'Dafür fehlt dir die Berechtigung.');
  }
  if (status === 404) {
    return t('errors.nicht_gefunden', 'Das wurde nicht gefunden — vielleicht wurde es inzwischen gelöscht.');
  }
  if (status !== undefined && status >= 500) {
    return t('errors.server_problem', 'Beim Server ist ein Problem aufgetreten (Status {status}). Versuch es später noch einmal.', { status });
  }

  console.error('Fehler ohne anzeigbaren Grund:', err);
  return t('errors.generic', 'Etwas ist schiefgelaufen.');
}

/**
 * Der Grund, erst beim Rendern formuliert — für Fehler, die ein Effekt beim
 * Mount in den State legt: `deferredText('…_grund', '…: {grund}', { grund:
 * grundBeimRendern(err) })`. Mit `fehlerText(err, t)` an dieser Stelle stünde
 * der Grund in der Sprache des `t` beim Fangen — beim Mount sind die
 * Übersetzungen noch nicht da, der Satz wäre übersetzt, der Grund deutsch.
 * Das Ergebnis wird je `t` gemerkt, damit ein Fehler ohne Grund nicht bei
 * jedem Rendern erneut in die Konsole geht.
 */
export function grundBeimRendern(err: unknown, feldNamen: FeldNamen = {}): (t: Uebersetzer) => string {
  let letztesT: Uebersetzer | undefined;
  let letzterText = '';
  return (t) => {
    if (t !== letztesT) {
      letzterText = fehlerText(err, t, feldNamen);
      letztesT = t;
    }
    return letzterText;
  };
}
